import { Title, Meta, Link } from '@solidjs/meta';
import { For, Show, type JSX } from 'solid-js';
import { SITE, absoluteUrl, type SeoData } from '@lib/seo';

// Per-route head metadata. Drop <PageHead ... /> at the top of any
// route component — the Meta/Title nodes get hoisted into the SSR HTML
// via @solidjs/meta's MetaProvider in src/app.tsx, so social scrapers
// that don't execute JS still see the right title and OG image.
//
// JSON-LD objects passed via `jsonLd` render inline as
// <script type="application/ld+json"> elements. Google's structured
// data parser reads these wherever they appear in the DOM, so we don't
// need to hoist them into <head>.

function renderJsonLd(data: unknown): JSX.Element {
  // Escape `</` to `\u003c/` so a string value containing "</script>"
  // can't break out of the script tag. JSON parsers accept the escaped
  // form transparently.
  const serialised = JSON.stringify(data).replace(/</g, '\\u003c');
  return <script type="application/ld+json" innerHTML={serialised} />;
}

export default function PageHead(props: SeoData): JSX.Element {
  const description = () => props.description ?? SITE.defaultDescription;
  const ogImage = () => absoluteUrl(props.ogImage ?? SITE.defaultOgImage);
  const canonical = () => (props.canonical ? absoluteUrl(props.canonical) : null);
  const jsonLdList = () => {
    const v = props.jsonLd;
    if (v == null) return [] as unknown[];
    return Array.isArray(v) ? v : [v];
  };

  return (
    <>
      <Title>{props.title}</Title>
      <Meta name="description" content={description()} />
      <Meta property="og:title" content={props.title} />
      <Meta property="og:description" content={description()} />
      <Meta property="og:image" content={ogImage()} />
      <Meta name="twitter:title" content={props.title} />
      <Meta name="twitter:description" content={description()} />
      <Meta name="twitter:image" content={ogImage()} />
      <Show when={canonical()}>
        <Link rel="canonical" href={canonical()!} />
        <Meta property="og:url" content={canonical()!} />
      </Show>
      <Show when={props.noIndex}>
        <Meta name="robots" content="noindex,nofollow" />
      </Show>
      <For each={jsonLdList()}>{(data) => renderJsonLd(data)}</For>
    </>
  );
}
